import { MAP_BORDER_SIZE } from '../constants';
import type { MapData, TileData } from '../types/map';

/**
 * Genera los tiles de bloqueo de borde de mapa (MAP_BORDER_SIZE tiles en cada lado).
 * Estos bordes NO se guardan en el JSON, se generan automáticamente al cargar.
 * Se agregan a una capa interna '_border' que no se renderiza pero sí se usa para colisiones.
 */
export function generateBorderTiles(bounds: MapData['bounds']): TileData[] {
  const tiles: TileData[] = [];
  const border = MAP_BORDER_SIZE;
  const { minX, maxX, minY, maxY } = bounds;

  // Borde superior e inferior
  for (let x = minX; x <= maxX; x += 1) {
    // Superior
    for (let y = minY; y < minY + border; y += 1) {
      tiles.push({
        x,
        y,
        sprite: 'border_limit',
        blocked: true,
      });
    }
    // Inferior
    for (let y = maxY - border + 1; y <= maxY; y += 1) {
      tiles.push({
        x,
        y,
        sprite: 'border_limit',
        blocked: true,
      });
    }
  }

  // Borde izquierdo y derecho (sin solapar esquinas que ya se añadieron)
  for (let y = minY + border; y <= maxY - border; y += 1) {
    // Izquierdo
    for (let x = minX; x < minX + border; x += 1) {
      tiles.push({
        x,
        y,
        sprite: 'border_limit',
        blocked: true,
      });
    }

    // Derecho
    for (let x = maxX - border + 1; x <= maxX; x += 1) {
      tiles.push({
        x,
        y,
        sprite: 'border_limit',
        blocked: true,
      });
    }
  }

  return tiles;
}

/**
 * Carga un MapData desde JSON y le añade automáticamente los tiles de borde.
 * Los bordes se añaden a una capa interna '_border' para colisiones.
 */
export function loadMapWithBorders(rawMapData: MapData): MapData {
  const borderTiles = generateBorderTiles(rawMapData.bounds);
  
  // Clonar el mapa para no mutar el original
  const mapData: MapData = JSON.parse(JSON.stringify(rawMapData));
  
  // Añadir capa de bordes (solo para colisiones, no se renderiza visualmente)
  mapData.layers.push({
    name: '_border',
    z: -1, // Z negativo para que no interfiera con el render
    tiles: borderTiles,
  });
  
  return mapData;
}

/**
 * Verifica si un tile está bloqueado en cualquier capa del mapa.
 */
export function isTileBlocked(mapData: MapData, x: number, y: number): boolean {
  for (const layer of mapData.layers) {
    const tile = layer.tiles.find((t) => t.x === x && t.y === y);
    if (tile && tile.blocked) {
      return true;
    }
  }
  return false;
}

/**
 * Prepara un MapData para guardarlo como JSON.
 * Remueve la capa de bordes (_border) ya que se regenera al cargar.
 */
export function prepareMapForSave(mapData: MapData): MapData {
  // Clonar el mapa para no mutar el original
  const cleanMapData: MapData = JSON.parse(JSON.stringify(mapData));
  
  // Filtrar la capa de bordes
  cleanMapData.layers = cleanMapData.layers.filter(layer => layer.name !== '_border');
  
  return cleanMapData;
}
